/**
 * PI-QUAL scoring annotation extension handler.
 *
 * PI-QUAL fields are persisted in annotation_data via the generic EAV model.
 */
import { BaseExtensionHandler } from "../_base.ts"
import type {
  ClientValidationContext,
  CompletionValidationContext,
  ExtensionManifest,
  SerializedAnnotation,
  StoredAnnotation
} from "../_interface.ts"
import {
  createPiqualSchema,
  PIQUAL_BOOLEAN_FIELDS,
  PIQUAL_FIELD_LABELS,
  PIQUAL_NUMERIC_FIELDS,
  piqualConfigSchema,
  piqualProjectConfigSchema,
  resolvePiqualConfig,
  updatePiqualSchema
} from "./schema.ts"

type PiqualPayload = Partial<
  Record<(typeof PIQUAL_BOOLEAN_FIELDS)[number], boolean> & Record<(typeof PIQUAL_NUMERIC_FIELDS)[number], number>
>

const PIQUAL_FIELDS = [...PIQUAL_BOOLEAN_FIELDS, ...PIQUAL_NUMERIC_FIELDS]

export class PiqualHandler extends BaseExtensionHandler {
  readonly type = "piqual"
  readonly manifest: ExtensionManifest = {
    type: "piqual",
    label: "PI-QUAL",
    scope: "case",
    storage: "structured",
    supportsLinking: false
  }

  override getConfigSchema() {
    return piqualConfigSchema
  }

  override getProjectConfigSchema() {
    return piqualProjectConfigSchema
  }

  getCreateSchema() {
    return createPiqualSchema
  }

  getUpdateSchema() {
    return updatePiqualSchema
  }

  serializeForDb(apiData: unknown): SerializedAnnotation {
    const payload = apiData as PiqualPayload
    const dataRows: SerializedAnnotation["data"] = []

    for (const field of PIQUAL_BOOLEAN_FIELDS) {
      const value = payload[field]
      if (value !== undefined) {
        dataRows.push({
          field_key: field,
          value_bool: value ? 1 : 0
        })
      }
    }

    for (const field of PIQUAL_NUMERIC_FIELDS) {
      const value = payload[field]
      if (value !== undefined) {
        dataRows.push({
          field_key: field,
          value_int: Number.isInteger(value) ? value : null,
          value_float: Number.isInteger(value) ? null : value
        })
      }
    }

    return {
      annotation: {},
      data: dataRows,
      files: []
    }
  }

  deserializeFromDb(stored: StoredAnnotation): unknown {
    const piqual: Record<string, boolean | number> = {}

    for (const d of stored.data) {
      if (!PIQUAL_FIELDS.includes(d.field_key as (typeof PIQUAL_FIELDS)[number])) {
        continue
      }

      if ((PIQUAL_BOOLEAN_FIELDS as readonly string[]).includes(d.field_key) && d.value_bool !== null) {
        piqual[d.field_key] = d.value_bool === 1
        continue
      }

      if ((PIQUAL_NUMERIC_FIELDS as readonly string[]).includes(d.field_key)) {
        if (d.value_int !== null) {
          piqual[d.field_key] = d.value_int
        } else if (d.value_float !== null) {
          piqual[d.field_key] = d.value_float
        }
      }
    }

    return {
      id: stored.annotation.id,
      submission_id: stored.annotation.submission_id,
      project_module_id: stored.annotation.project_module_id,
      series_id: stored.annotation.series_id,
      ...piqual,
      created_at: stored.annotation.created_at,
      updated_at: stored.annotation.updated_at
    }
  }

  getExportColumns(_templateSchema: unknown): string[] {
    return [...PIQUAL_FIELDS]
  }

  toExportRow(annotations: StoredAnnotation[], _templateSchema: unknown): Record<string, string | number> {
    const result: Record<string, string | number> = {}

    for (const field of PIQUAL_FIELDS) {
      result[field] = ""
    }

    for (const annotation of annotations) {
      for (const d of annotation.data) {
        if ((PIQUAL_BOOLEAN_FIELDS as readonly string[]).includes(d.field_key)) {
          if (d.value_bool !== null) {
            result[d.field_key] = d.value_bool
          }
          continue
        }

        if ((PIQUAL_NUMERIC_FIELDS as readonly string[]).includes(d.field_key)) {
          if (d.value_int !== null) {
            result[d.field_key] = d.value_int
          } else if (d.value_float !== null) {
            result[d.field_key] = d.value_float
          }
        }
      }
    }

    return result
  }

  override validateForCompletion(
    annotations: StoredAnnotation[],
    _templateSchema: unknown,
    context: CompletionValidationContext
  ): { valid: boolean; errors: string[] } {
    const { requiredFields, allowEmpty } = resolvePiqualConfig(context.projectModuleConfigJson)
    const errors: string[] = []

    if (annotations.length === 0) {
      if (!allowEmpty) {
        errors.push("PI-QUAL: no answers recorded")
      }
      return { valid: errors.length === 0, errors }
    }

    // PI-QUAL has at most one annotation row per submission.
    for (const row of annotations) {
      const byKey = new Map<string, boolean | null>()
      for (const d of row.data) {
        if ((PIQUAL_BOOLEAN_FIELDS as readonly string[]).includes(d.field_key)) {
          byKey.set(d.field_key, d.value_bool === null ? null : d.value_bool === 1)
        }
      }

      const hasDceAnswered = byKey.has("has_dce")
      const hasDce = byKey.get("has_dce") === true

      for (const field of requiredFields) {
        const isDceSubQuestion = field === "dce_artifacts" || field === "dce_structures"
        if (isDceSubQuestion) {
          // Only required when DCE is present.
          if (!hasDceAnswered) continue // `has_dce` error will surface separately if it's required.
          if (!hasDce) continue
        }
        if (!byKey.has(field) || byKey.get(field) === null) {
          errors.push(`PI-QUAL: ${PIQUAL_FIELD_LABELS[field]} is unanswered`)
        }
      }
    }

    return { valid: errors.length === 0, errors }
  }

  override validateCompletionClient(context: ClientValidationContext): { valid: boolean; errors: string[] } {
    const { module, scores } = context
    const { requiredFields, allowEmpty } = resolvePiqualConfig(module.config)

    const row = scores.find((s) => s.projectModuleId === module.id && s.labelName === null)
    if (!row) {
      if (allowEmpty) return { valid: true, errors: [] }
      return { valid: false, errors: ["PI-QUAL: no answers recorded"] }
    }

    const errors: string[] = []
    const values = row.values
    const hasDceAnswered = typeof values.has_dce === "boolean"
    const hasDce = values.has_dce === true

    for (const field of requiredFields) {
      const isDceSubQuestion = field === "dce_artifacts" || field === "dce_structures"
      if (isDceSubQuestion) {
        if (!hasDceAnswered) continue
        if (!hasDce) continue
      }
      if (typeof values[field] !== "boolean") {
        errors.push(`PI-QUAL: ${PIQUAL_FIELD_LABELS[field]} is unanswered`)
      }
    }

    return { valid: errors.length === 0, errors }
  }
}
